'use client';

import { ReactNode, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '@/lib/auth';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Users, Building2, Settings, LogOut, User, AlertTriangle, XCircle, Layers, AlertCircle, Moon, Sun, Menu, Home, Shield, ShieldAlert, Lock } from 'lucide-react';
import { useRouter, usePathname } from 'next/navigation';
import { ProfileDialog } from '@/components/profile-dialog';
import { SelectTenantsDialog } from '@/components/select-tenants-dialog';
import type { TenantAccessDto } from '@/lib/generated-api';
import { validateSession } from '@/lib/session-management';
import { getShouldRunValidation, VALIDATION_GRACE_MS } from '@/lib/auth-state-machine';
import { formatInUserTimezone, formatTimeRemaining, clearSessionAndRedirectToLogin } from '@/lib/utils';
import { useTimezone } from '@/hooks/use-timezone';
import { useToast } from '@/components/ui/use-toast';
import { useSessionService } from '@/lib/use-api-lib';
import { logger } from '@/lib/logger';
import { useTheme } from '@/lib/theme-context';
import React from 'react';
import { getBackgroundClasses, getTextClasses, getBorderClasses, getInputClasses, getAccentClasses } from '@/lib/theme-utils';
import { getThemeClass } from '@/lib/theme-helpers';
import { useIsMobile } from '@/components/ui/use-mobile';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';

interface AppLayoutProps {
  children: ReactNode;
}

export const AppLayout = React.memo(function AppLayout({ children }: AppLayoutProps) {
  const { 
    user, 
    session,
    availableTenants,
    currentTenant,
    switchTenant,
    ensureTenantAccess,
    logout,
    updateSession,
    sessionValidationStartedAtMs,
    isSessionValid,
  } = useAuth();

  // Debug logging for availableTenants changes
  useEffect(() => {
    logger.log(' [APP-LAYOUT] availableTenants changed:', {
      count: availableTenants?.length || 0,
      tenants: availableTenants?.map(t => ({ id: t.tenantId, name: t.tenantName })) || [],
      shouldShowSwitchButton: availableTenants && availableTenants.length > 1
    });
  }, [availableTenants]);
  
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [selectTenantsDialogOpen, setSelectTenantsDialogOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sessionStatus, setSessionStatus] = useState<'active' | 'expired' | 'revoked' | 'invalid' | 'checking'>('checking');
  const { theme, setTheme, availableThemes, toggleTheme } = useTheme();
  const router = useRouter();
  const pathname = usePathname();
  const { userTimezone } = useTimezone();
  const { toast } = useToast();
  const { sessionService } = useSessionService();
  const isMobile = useIsMobile();

  // Header / profile label: OIDC user when present; otherwise Aegis session profile (hard refresh).
  const displayUserName = useMemo(() => {
    const oidc = user?.profile;
    const sessionProfile = session?.profile;
    return (
      oidc?.preferred_username ||
      oidc?.name ||
      oidc?.email ||
      sessionProfile?.preferredUsername ||
      sessionProfile?.name ||
      sessionProfile?.email ||
      'User'
    );
  }, [user?.profile, session?.profile]);

  const displayUserEmail = useMemo(
    () => user?.profile?.email || session?.profile?.email || '',
    [user?.profile?.email, session?.profile?.email]
  );

  const displayUserAvatar = user?.profile?.picture || session?.profile?.picture;

  // Memoize session validation to prevent unnecessary calls
  const sessionId = session?.sessionId;
  const sessionValidationKey = useMemo(() => sessionId || '', [sessionId]);
  const sessionCreatedAtMs = sessionValidationStartedAtMs > 0
    ? sessionValidationStartedAtMs
    : (session?.createdAt ? new Date(session.createdAt).getTime() : 0);
  const validationRunIdRef = useRef(0);

  // Check session status periodically - but only if session exists
  useEffect(() => {
    // If no session ID yet, keep status as 'checking' (don't mark as invalid immediately)
    // The session might still be loading
    if (!sessionValidationKey) {
      setSessionStatus('checking');
      return;
    }

    const runId = ++validationRunIdRef.current;
    let isComponentMounted = true;
    let retryTimeoutId: ReturnType<typeof setTimeout> | undefined;
    // Do not force "checking" for the whole grace window: auth treats the session as valid until
    // server validation runs (see lib/auth.tsx). Showing checking here left the badge stuck for VALIDATION_GRACE_MS.
    // Aegis session + isSessionValid is enough — do not require OIDC `user` (missing after hard refresh).
    if (sessionValidationKey && isSessionValid) {
      setSessionStatus('active');
    }
  
    const checkSessionStatus = async () => {
      if (!sessionValidationKey || !isComponentMounted) {
        return;
      }
      // Respect grace period after session creation so we don't validate a brand-new session immediately
      if (!getShouldRunValidation(sessionValidationKey, sessionCreatedAtMs)) {
        if (isSessionValid || sessionValidationKey) {
          setSessionStatus('active');
        }
        return;
      }
  
      try {
        setSessionStatus('checking');
        const result = await validateSession(sessionValidationKey);
        if (!isComponentMounted || validationRunIdRef.current !== runId) return; // Prevent stale state updates
        
        if (result.isValid) {
          setSessionStatus('active');
        } else if (result.reason === 'expired') {
          setSessionStatus('expired');
          // Handle expired session - redirect to login with Keycloak logout
          logger.log(' [APP-LAYOUT] Session expired, logging out from Keycloak');
          logout(); // This will properly log out from Keycloak
        } else if (result.reason === 'revoked') {
          setSessionStatus('revoked');
          // Handle revoked session - redirect to login with Keycloak logout
          logger.log(' [APP-LAYOUT] Session revoked/cleaned up, logging out from Keycloak');
          logout(); // This will properly log out from Keycloak
        } else if (result.reason === 'token_not_ready') {
          // Token can lag slightly behind session restoration in production.
          // Keep checking and retry quickly instead of staying indefinitely.
          // If auth already considers the session valid, show active rather than a stuck gray badge.
          if (isSessionValid) {
            setSessionStatus('active');
          } else {
            setSessionStatus('checking');
          }
          if (retryTimeoutId !== undefined) {
            clearTimeout(retryTimeoutId);
          }
          retryTimeoutId = setTimeout(() => {
            if (isComponentMounted && validationRunIdRef.current === runId && sessionValidationKey) {
              checkSessionStatus();
            }
          }, 1500);
        } else if (result.reason === 'error') {
          // If validation failed due to missing auth token or other error,
          // prefer auth's isSessionValid over an indefinite gray "Checking" badge.
          if (isSessionValid) {
            logger.log(' [APP-LAYOUT] Validation error but auth session valid — showing active:', result.message);
            setSessionStatus('active');
          } else if (sessionValidationKey) {
            logger.log(' [APP-LAYOUT] Validation error but session exists, keeping as checking:', result.message);
            setSessionStatus('checking');
          } else {
            setSessionStatus('invalid');
          }
        } else {
          // Unknown reason - keep as checking rather than invalid
          logger.log(' [APP-LAYOUT] Unknown validation result reason:', result.reason);
          setSessionStatus(isSessionValid ? 'active' : 'checking');
        }
      } catch (error) {
        if (!isComponentMounted || validationRunIdRef.current !== runId) return;
        console.error('Failed to check session status:', error);
        // On network errors, if we have a session, keep as checking rather than invalid
        // The error might be temporary
        if (isSessionValid) {
          setSessionStatus('active');
        } else if (sessionValidationKey) {
          logger.log(' [APP-LAYOUT] Validation error but session exists, keeping as checking');
          setSessionStatus('checking');
        } else {
          setSessionStatus('invalid');
        }
      }
    };

    // First check: immediately if past grace period, otherwise after grace period (avoid validating brand-new session)
    let graceTimeoutId: ReturnType<typeof setTimeout> | undefined;
    if (getShouldRunValidation(sessionValidationKey, sessionCreatedAtMs)) {
      checkSessionStatus();
    } else {
      const delayMs = Math.max(0, VALIDATION_GRACE_MS - (Date.now() - sessionCreatedAtMs));
      graceTimeoutId = setTimeout(() => {
        if (isComponentMounted && sessionValidationKey) checkSessionStatus();
      }, delayMs);
    }

    // Reduce frequency to every 5 minutes to prevent excessive API calls (we also validate on navigation)
    // Only run when tab is visible to save CPU
    const interval = setInterval(() => {
      if (isComponentMounted && sessionValidationKey && !document.hidden) {
        checkSessionStatus();
      }
    }, 300000);

    // Listen for visibility changes to pause/resume validation
    const handleVisibilityChange = () => {
      if (!document.hidden && isComponentMounted && sessionValidationKey) {
        // Validate immediately when tab becomes visible
        checkSessionStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isComponentMounted = false;
      if (graceTimeoutId !== undefined) clearTimeout(graceTimeoutId);
      if (retryTimeoutId !== undefined) clearTimeout(retryTimeoutId);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [sessionValidationKey, sessionCreatedAtMs, logout, isSessionValid]);

  // Memoize session status badge to prevent recreation
  const sessionStatusBadge = useMemo(() => {
    switch (sessionStatus) {
      case 'active':
        return (
          <Badge variant="outline" className={`text-xs ${getThemeClass(theme, 'bg-green-500/10 text-green-300 border-green-500/30', 'bg-green-100 text-green-700 border-green-300')}`}>
            <div className={`h-1.5 w-1.5 rounded-full ${getThemeClass(theme, 'bg-green-500', 'bg-green-600')} mr-1 animate-pulse`}></div>
            Active Session
          </Badge>
        );
      case 'expired':
        return (
          <Badge variant="outline" className={`text-xs ${getThemeClass(theme, 'bg-amber-500/10 text-amber-300 border-amber-500/30', 'bg-amber-100 text-amber-700 border-amber-300')}`}>
            <AlertTriangle className="w-3 h-3 mr-1" />
            Session Expired
          </Badge>
        );
      case 'revoked':
        return (
          <Badge variant="outline" className={`text-xs ${getThemeClass(theme, 'bg-red-500/10 text-red-300 border-red-500/30', 'bg-red-100 text-red-700 border-red-300')}`}>
            <XCircle className="w-3 h-3 mr-1" />
            Session Revoked
          </Badge>
        );
      case 'invalid':
        return (
          <Badge variant="outline" className={`text-xs ${getThemeClass(theme, 'bg-red-500/10 text-red-300 border-red-500/30', 'bg-red-100 text-red-700 border-red-300')}`}>
            <XCircle className="w-3 h-3 mr-1" />
            Invalid Session
          </Badge>
        );
      case 'checking':
        return (
          <Badge variant="outline" className={`text-xs ${getTextClasses(theme)}`}>
            <div className={`h-1.5 w-1.5 rounded-full ${getThemeClass(theme, 'bg-slate-500', 'bg-slate-600')} mr-1 animate-pulse`}></div>
            Checking Session
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className={`text-xs ${getTextClasses(theme)}`}>
            Unknown Status
          </Badge>
        );
    }
  }, [sessionStatus, theme]);

  // Memoize connection status badge
  const connectionStatusBadge = useMemo(() => {
    switch (sessionStatus) {
      case 'active':
        return (
          <Badge variant="outline" className={`text-xs ${getThemeClass(theme, 'bg-green-500/10 text-green-300 border-green-500/30', 'bg-green-100 text-green-700 border-green-300')}`}>
            <div className={`h-1.5 w-1.5 rounded-full ${getThemeClass(theme, 'bg-green-500', 'bg-green-600')} mr-1 animate-pulse`}></div>
            Connected
          </Badge>
        );
      case 'expired':
        return (
          <Badge variant="outline" className={`text-xs ${getThemeClass(theme, 'bg-amber-500/10 text-amber-300 border-amber-500/30', 'bg-amber-100 text-amber-700 border-amber-300')}`}>
            <AlertTriangle className="w-3 h-3 mr-1" />
            Expired
          </Badge>
        );
      case 'revoked':
        return (
          <Badge variant="outline" className={`text-xs ${getThemeClass(theme, 'bg-red-500/10 text-red-300 border-red-500/30', 'bg-red-100 text-red-700 border-red-300')}`}>
            <XCircle className="w-3 h-3 mr-1" />
            Revoked
          </Badge>
        );
      case 'invalid':
        return (
          <Badge variant="outline" className={`text-xs ${getThemeClass(theme, 'bg-red-500/10 text-red-300 border-red-500/30', 'bg-red-100 text-red-700 border-red-300')}`}>
            <XCircle className="w-3 h-3 mr-1" />
            Disconnected
          </Badge>
        );
      case 'checking':
        return (
          <Badge variant="outline" className={`text-xs ${getTextClasses(theme)}`}>
            <div className={`h-1.5 w-1.5 rounded-full ${getThemeClass(theme, 'bg-slate-500', 'bg-slate-600')} mr-1 animate-pulse`}></div>
            Checking
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className={`text-xs ${getTextClasses(theme)}`}>
            Unknown
          </Badge>
        );
    }
  }, [sessionStatus, theme]);

  // Memoize formatted expiry time to prevent constant recalculation
  const formattedExpiryTime = useMemo(() => {
    if (!session?.expiresAt) return 'Never';
    
    try {
      const formattedTime = formatInUserTimezone(session.expiresAt, userTimezone, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZoneName: 'short'
      });
      
      const remainingTime = formatTimeRemaining(new Date(session.expiresAt).getTime());
      return `${formattedTime} (${remainingTime})`;
    } catch (error) {
      console.error('Error formatting expiry time:', error);
      return 'Invalid date';
    }
  }, [session?.expiresAt, userTimezone]);

  // Memoize tenant switch handler
  const handleTenantSwitch = useCallback(async (tenantId: string) => {
    logger.log(' [TENANT-SWITCH] Starting tenant switch to:', tenantId);
    logger.log(' [TENANT-SWITCH] Current tenant:', currentTenant?.tenantId);
    
    if (tenantId === currentTenant?.tenantId) {
      logger.log(' [TENANT-SWITCH] Already on target tenant, skipping');
      return;
    }

    logger.log(' [TENANT-SWITCH] Calling switchTenant...');
    const updatedSession = await switchTenant(tenantId);
    logger.log(' [TENANT-SWITCH] switchTenant completed, updated session:', updatedSession);

    // If switch was skipped (already in progress), we get the old session — don't navigate or toast
    if (updatedSession?.currentTenant?.tenantId !== tenantId) {
      logger.log(' [TENANT-SWITCH] Switch did not complete (got old session), skipping navigation and toast');
      return;
    }

    // Give the auth layer a brief moment to settle currentTenant before path canonicalization
    await new Promise((r) => setTimeout(r, 50));

    // After successful switch, canonicalize the URL slug to the new tenant using returned session for freshness
    const target = (updatedSession?.availableTenants || []).find((t) => t.tenantId === tenantId) ||
                   availableTenants?.find((t) => t.tenantId === tenantId);
    const newSlug = target?.tenantSlug || updatedSession?.currentTenant?.tenantSlug;
    const newName = target?.tenantName || updatedSession?.currentTenant?.tenantName || newSlug || tenantId;
    if (newSlug) {
      const slugPrefix = '/t/';
      let newPath = pathname || '/';
      if (newPath.startsWith(slugPrefix)) {
        const parts = newPath.split('/');
        // ['', 't', '{slug}', ...rest]
        if (parts.length >= 3) {
          parts[2] = newSlug;
          newPath = parts.join('/');
        } else {
          newPath = slugPrefix + newSlug;
        }
      } else {
        newPath = slugPrefix + newSlug;
      }
      router.replace(newPath);
    } else {
      // As a fallback, refresh to ensure UI reflects new tenant
      router.refresh();
    }

    // Surface a brief toast confirming the switch
    try {
      toast({
        title: 'Tenant switched',
        description: `Now working in ${newName}`,
      });
    } catch {}
  }, [availableTenants, currentTenant?.tenantId, pathname, router, switchTenant, ensureTenantAccess]);

  // Memoize navigation handlers
  const handleSettingsNavigation = useCallback(() => {
    router.push('/settings');
  }, [router]);

  // Calculate selectable tenants (from session or availableTenants)
  const selectableTenants = useMemo(() => {
    if (!session?.selectableTenantIds || !availableTenants) {
      return [];
    }
    const selectableIds = new Set(session.selectableTenantIds);
    return availableTenants.filter((t) => t.tenantId && selectableIds.has(t.tenantId));
  }, [session?.selectableTenantIds, availableTenants]);

  // Honor whatever selectedTenantIds the backend provides in the session
  // Backend handles default logic based on configuration (ALL_SELECTABLE vs CURRENT_ONLY)
  const selectedTenantIds = useMemo(() => {
    return session?.selectedTenantIds || [];
  }, [session?.selectedTenantIds]);

  // Handle confirming tenant selection
  const handleSelectTenants = useCallback(
    async (selectedIds: string[]) => {
      if (!sessionService || !session?.sessionId) {
        toast({
          title: 'Error',
          description: 'Session service not available.',
          variant: 'destructive',
        });
        return;
      }

      try {
        const response = await sessionService.selectTenants({
          sessionId: session.sessionId,
          updateTenantSelectionRequest: {
            selectedTenantIds: selectedIds,
          },
        });

        if (response.success && response.data) {
          // Update the session state with the returned full session data
          // The select-tenants endpoint returns the complete session with updated selectedTenantIds
          updateSession(response.data);
          
          toast({
            title: 'Tenants selected',
            description: `Data will be aggregated from ${selectedIds.length} tenant(s). Refreshing current view...`,
          });
          
          // Trigger events for components that listen to them
          window.dispatchEvent(new Event('tenantBindingsChanged'));
          // Note: dataRefreshTrigger is incremented in updateSession, which will cause
          // all components watching it to automatically refetch data with the new tenant filter
          // No hard refresh needed - components will refetch automatically
        } else {
          throw new Error(response.error || 'Failed to update tenant selection');
        }
      } catch (error) {
        // Avoid console noise for expected backend "Session has expired" (dialog shows toast)
        if (!(error instanceof Error) || (error.message !== 'Session has expired' && !error.message.includes('Session has expired'))) {
          console.error('Failed to select tenants:', error);
        }
        throw error;
      }
    },
    [sessionService, session?.sessionId, toast, router, updateSession]
  );
  
  // Debug logging (only in development)
  useEffect(() => {
    logger.log(' [APP-LAYOUT] Auth state changed:', {
      hasUser: !!user,
      hasSession: !!session,
      hasCurrentTenant: !!currentTenant,
      availableTenantsCount: availableTenants?.length || 0,
      sessionId: session?.sessionId,
      currentTenantId: currentTenant?.tenantId,
      sessionStatus
    });
  }, [user, session, currentTenant, availableTenants, sessionStatus]);
  
  return (
    <div className={`flex h-dvh min-h-0 flex-col overflow-hidden ${getBackgroundClasses(theme, 'page')}`}>
      {/* Multi-Tenant Header Bar - Fixed at top */}
      <div className={`fixed top-0 left-0 right-0 w-screen z-50 border-b m-0 p-0 ${getBackgroundClasses(theme, 'default')}`} style={{ backgroundColor: 'var(--theme-header-footer)' }}>
        <div className={`max-w-7xl mx-auto ${isMobile ? 'px-2 py-1.5' : 'px-4 py-2'}`}>
          <div className="flex items-center justify-between">
            {/* Current Tenant Info */}
            <div className={`flex items-center ${isMobile ? 'space-x-2 min-w-0 flex-1' : 'space-x-3'}`}>
              <div className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} rounded-lg flex items-center justify-center flex-shrink-0`} style={{ background: 'linear-gradient(to bottom right, var(--theme-accent), var(--theme-info))' }}>
                <Building2 className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-white`} />
              </div>
              <div className={`${isMobile ? 'min-w-0 flex-1' : ''}`}>
                <div className={`${isMobile ? 'text-xs' : 'text-sm'} font-semibold ${getTextClasses(theme)} truncate`}>
                  {currentTenant?.tenantName || 'Loading...'}
                </div>
                {!isMobile && (
                  <div className={`text-xs ${getTextClasses(theme, 'muted')}`}>
                    {displayUserName}
                  </div>
                )}
              </div>
              {!isMobile && sessionStatusBadge}
            </div>

            {/* Desktop Actions */}
            {!isMobile && (
              <div className="flex items-center space-x-3">
                {/* Select Tenants Button (visible when multiple selectable tenants) */}
                {selectableTenants.length > 1 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectTenantsDialogOpen(true)}
                    className={`${getThemeClass(theme, 
                      `text-slate-300 border-slate-600 bg-slate-800/50 hover:bg-slate-700/50 ${selectedTenantIds.length === 0 ? '/50 /10' : ''}`,
                      `text-slate-700 border-slate-300 bg-white hover:bg-slate-50 ${selectedTenantIds.length === 0 ? 'bg-yellow-50' : ''}`
                    )}`}
                    style={{ borderColor: 'var(--theme-table-header-divider)' }}
                  >
                    <Layers className="w-4 h-4 mr-2" />
                    Select Tenants
                    {selectedTenantIds.length === 0 ? (
                      <AlertCircle className={`w-3 h-3 ml-2 ${getThemeClass(theme, 'text-yellow-400', 'text-yellow-600')}`} />
                    ) : selectedTenantIds.length > 1 ? (
                      <Badge className={`ml-2 ${getThemeClass(theme, 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30', 'bg-cyan-100 text-cyan-700 border-cyan-300')}`}>
                        {selectedTenantIds.length}
                      </Badge>
                    ) : null}
                  </Button>
                )}

                {/* Tenant Dropdown (visible when multiple tenants OR at least one available and none selected) */}
                {availableTenants && (availableTenants.length > 1 || (availableTenants.length >= 1 && !currentTenant)) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className={`${getThemeClass(theme,
                          'text-slate-300 border-slate-600 bg-slate-800/50 hover:bg-slate-700/50',
                          'text-slate-700 border-slate-300 bg-white hover:bg-slate-50'
                        )}`}
                        style={{ borderColor: 'var(--theme-table-header-divider)' }}
                      >
                        <Users className="w-4 h-4 mr-2" />
                        Switch Tenant
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className={`w-80 ${getBackgroundClasses(theme, 'default')}`} align="end">
                      <DropdownMenuLabel className={getTextClasses(theme)}>Available Tenants</DropdownMenuLabel>
                      <DropdownMenuSeparator className={getThemeClass(theme, 'bg-slate-700', 'bg-slate-300')} />
                      {availableTenants.map((tenant: TenantAccessDto) => (
                        <DropdownMenuItem
                          key={tenant.tenantId}
                          onClick={() => handleTenantSwitch(tenant.tenantId!)}
                          className={`flex items-center space-x-3 p-3 cursor-pointer ${
                            getThemeClass(theme,
                              `text-slate-300 hover:bg-slate-700 ${tenant.tenantId === currentTenant?.tenantId ? '/50' : ''}`,
                              `text-slate-700 hover:bg-slate-100 ${tenant.tenantId === currentTenant?.tenantId ? 'bg-slate-100' : ''}`
                            )
                          }`}
                        >
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(to bottom right, var(--theme-info), var(--theme-info))' }}>
                            <Building2 className="w-4 h-4 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm font-medium truncate ${getTextClasses(theme)}`}>
                              {tenant.tenantName}
                            </div>
                            <div className={`text-xs truncate ${getTextClasses(theme, 'muted')}`}>
                              {tenant.tenantSlug}
                            </div>
                          </div>
                          <div className="flex flex-col items-end space-y-1">
                            {tenant.tenantId === currentTenant?.tenantId && (
                              <Badge className={`text-xs ${getThemeClass(theme, 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30', 'bg-cyan-100 text-cyan-700 border-cyan-300')}`}>
                                Current
                              </Badge>
                            )}
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${
                                tenant.isActive 
                                  ? (getThemeClass(theme, 'bg-green-500/10 text-green-300 border-green-500/30', 'bg-green-100 text-green-700 border-green-300'))
                                  : (getThemeClass(theme, 'bg-yellow-500/10 text-yellow-300 border-yellow-500/30', 'bg-yellow-100 text-yellow-700 border-yellow-300'))
                              }`}
                            >
                              {tenant.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {/* Light/Dark Toggle Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleTheme}
                  className={`p-0 ${getTextClasses(theme)}`}
                  title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
                >
                  {theme === 'light' ? (
                    <Moon className="w-6 h-6" />
                  ) : (
                    <Sun className="w-6 h-6" />
                  )}
                </Button>

                {/* User Icon Profile/Settings Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className={`p-0 ${getTextClasses(theme)
                      }`}
                    >
                      <User className="w-6 h-6" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className={`w-56 ${getBackgroundClasses(theme, 'default')}`} align="end">
                    <DropdownMenuLabel className={getTextClasses(theme)}>Account</DropdownMenuLabel>
                    <DropdownMenuSeparator className={getThemeClass(theme, 'bg-slate-700', 'bg-slate-300')} />
                    <DropdownMenuItem
                      className={`cursor-pointer ${getTextClasses(theme)}`}
                      onClick={handleSettingsNavigation}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      <span>Settings</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className={getThemeClass(theme, 'bg-slate-700', 'bg-slate-300')} />
                    <DropdownMenuItem 
                      onClick={logout}
                      className={`cursor-pointer ${getThemeClass(theme, 'text-red-400 hover:bg-slate-700', 'text-red-600 hover:bg-slate-100')}`}
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      <span>Sign Out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className={`p-1.5 ${getTextClasses(theme)}`}
                  >
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className={`w-[280px] ${getBackgroundClasses(theme, 'default')} ${getBorderClasses(theme)}`}>
                  <SheetHeader>
                    <SheetTitle className={getTextClasses(theme)}>Menu</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6 space-y-4">
                    {/* Session Status on Mobile */}
                    <div className="pb-4 border-b">
                      {sessionStatusBadge}
                    </div>

                    {/* Navigation Links */}
                    <div className="space-y-2">
                      <Button
                        variant="ghost"
                        className={`w-full justify-start ${pathname === '/dashboard' ? getAccentClasses(theme, 'primary') : getTextClasses(theme)}`}
                        onClick={() => {
                          router.push('/dashboard');
                          setMobileMenuOpen(false);
                        }}
                      >
                        <Home className="w-4 h-4 mr-2" />
                        Dashboard
                      </Button>
                      <Button
                        variant="ghost"
                        className={`w-full justify-start ${pathname?.startsWith('/mdr') ? getAccentClasses(theme, 'primary') : getTextClasses(theme)}`}
                        onClick={() => {
                          router.push('/mdr');
                          setMobileMenuOpen(false);
                        }}
                      >
                        <Shield className="w-4 h-4 mr-2" />
                        MDR
                      </Button>
                      <Button
                        variant="ghost"
                        className={`w-full justify-start ${pathname?.startsWith('/vipr') ? getAccentClasses(theme, 'primary') : getTextClasses(theme)}`}
                        onClick={() => {
                          router.push('/vipr');
                          setMobileMenuOpen(false);
                        }}
                      >
                        <ShieldAlert className="w-4 h-4 mr-2" />
                        Vulnerability Management
                      </Button>
                      <Button
                        variant="ghost"
                        className={`w-full justify-start ${pathname?.startsWith('/admin') ? getAccentClasses(theme, 'primary') : getTextClasses(theme)}`}
                        onClick={() => {
                          router.push('/admin');
                          setMobileMenuOpen(false);
                        }}
                      >
                        <Lock className="w-4 h-4 mr-2" />
                        Administration
                      </Button>
                      <Button
                        variant="ghost"
                        className={`w-full justify-start ${pathname?.startsWith('/settings') ? getAccentClasses(theme, 'primary') : getTextClasses(theme)}`}
                        onClick={() => {
                          router.push('/settings');
                          setMobileMenuOpen(false);
                        }}
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Settings
                      </Button>
                    </div>

                    <div className="pt-4 border-t space-y-2">
                      {/* Select Tenants Button (mobile) */}
                      {selectableTenants.length > 1 && (
                        <Button
                          variant="outline"
                          className={`w-full justify-start ${getTextClasses(theme)}`}
                          style={{ borderColor: 'var(--theme-table-header-divider)' }}
                          onClick={() => {
                            setSelectTenantsDialogOpen(true);
                            setMobileMenuOpen(false);
                          }}
                        >
                          <Layers className="w-4 h-4 mr-2" />
                          Select Tenants
                          {selectedTenantIds.length === 0 ? (
                            <AlertCircle className={`w-3 h-3 ml-2 ${getThemeClass(theme, 'text-yellow-400', 'text-yellow-600')}`} />
                          ) : selectedTenantIds.length > 1 ? (
                            <Badge className={`ml-2 ${getThemeClass(theme, 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30', 'bg-cyan-100 text-cyan-700 border-cyan-300')}`}>
                              {selectedTenantIds.length}
                            </Badge>
                          ) : null}
                        </Button>
                      )}

                      {/* Tenant Switcher (mobile) */}
                      {availableTenants && (availableTenants.length > 1 || (availableTenants.length >= 1 && !currentTenant)) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              className={`w-full justify-start ${getTextClasses(theme)}`}
                              style={{ borderColor: 'var(--theme-table-header-divider)' }}
                            >
                              <Users className="w-4 h-4 mr-2" />
                              Switch Tenant
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className={`w-64 ${getBackgroundClasses(theme, 'default')}`} align="start">
                            <DropdownMenuLabel className={getTextClasses(theme)}>Available Tenants</DropdownMenuLabel>
                            <DropdownMenuSeparator className={getThemeClass(theme, 'bg-slate-700', 'bg-slate-300')} />
                            {availableTenants.map((tenant: TenantAccessDto) => (
                              <DropdownMenuItem
                                key={tenant.tenantId}
                                onClick={() => {
                                  handleTenantSwitch(tenant.tenantId!);
                                  setMobileMenuOpen(false);
                                }}
                                className={`flex items-center space-x-3 p-3 cursor-pointer ${
                                  getThemeClass(theme,
                                    `text-slate-300 hover:bg-slate-700 ${tenant.tenantId === currentTenant?.tenantId ? '/50' : ''}`,
                                    `text-slate-700 hover:bg-slate-100 ${tenant.tenantId === currentTenant?.tenantId ? 'bg-slate-100' : ''}`
                                  )
                                }`}
                              >
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(to bottom right, var(--theme-info), var(--theme-info))' }}>
                                  <Building2 className="w-4 h-4 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className={`text-sm font-medium truncate ${getTextClasses(theme)}`}>
                                    {tenant.tenantName}
                                  </div>
                                  <div className={`text-xs truncate ${getTextClasses(theme, 'muted')}`}>
                                    {tenant.tenantSlug}
                                  </div>
                                </div>
                                {tenant.tenantId === currentTenant?.tenantId && (
                                  <Badge className={`text-xs ${getThemeClass(theme, 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30', 'bg-cyan-100 text-cyan-700 border-cyan-300')}`}>
                                    Current
                                  </Badge>
                                )}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}

                      {/* Theme Toggle (mobile) */}
                      <Button
                        variant="outline"
                        className={`w-full justify-start ${getTextClasses(theme)}`}
                        onClick={toggleTheme}
                      >
                        {theme === 'light' ? (
                          <>
                            <Moon className="w-4 h-4 mr-2" />
                            Dark Mode
                          </>
                        ) : (
                          <>
                            <Sun className="w-4 h-4 mr-2" />
                            Light Mode
                          </>
                        )}
                      </Button>

                      {/* User Profile (mobile) */}
                      <Button
                        variant="outline"
                        className={`w-full justify-start ${getTextClasses(theme)}`}
                        onClick={() => {
                          setProfileDialogOpen(true);
                          setMobileMenuOpen(false);
                        }}
                      >
                        <User className="w-4 h-4 mr-2" />
                        Profile
                      </Button>

                      {/* Sign Out (mobile) */}
                      <Button
                        variant="outline"
                        className={`w-full justify-start ${getThemeClass(theme, 'text-red-400 border-red-500/30 hover:bg-red-500/10', 'text-red-600 border-red-300 hover:bg-red-50')}`}
                        onClick={() => {
                          logout();
                          setMobileMenuOpen(false);
                        }}
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out
                      </Button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            )}
          </div>
        </div>
      </div>

      {/* Main Content - Adjusted for fixed header, footer, and mobile nav */}
      <main
        className={`relative flex min-h-0 flex-1 flex-col outline-none focus:outline-none focus-visible:outline-none ${isMobile ? 'pt-14 pb-32' : 'pt-20 pb-16'} ${getBackgroundClasses(theme, 'page')} overflow-x-hidden overflow-y-auto`}
        style={
          {
            background: 'var(--theme-page-background)',
            /* pt-14 + pb-32 (mobile) or pt-20 + pb-16 (desktop) — children use for max-height to avoid main scroll */
            '--app-main-vertical-padding': isMobile ? '11.5rem' : '9rem',
          } as React.CSSProperties
        }
      >
        {children}
      </main>

      {/* Mobile Bottom Navigation - Positioned above footer */}
      {isMobile && (
        <nav className={`fixed left-0 right-0 z-40 border-t ${getBackgroundClasses(theme, 'default')} ${getBorderClasses(theme)}`} style={{ bottom: '60px' }}>
          <div className="flex justify-around items-center py-1.5">
            <Button
              variant="ghost"
              size="sm"
              className={`flex flex-col items-center space-y-0.5 ${pathname === '/dashboard' ? getAccentClasses(theme, 'primary') : getTextClasses(theme, 'muted')}`}
              onClick={() => router.push('/dashboard')}
            >
              <Home className="w-5 h-5" />
              <span className="text-[10px]">Dashboard</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={`flex flex-col items-center space-y-0.5 ${pathname?.startsWith('/mdr') ? getAccentClasses(theme, 'primary') : getTextClasses(theme, 'muted')}`}
              onClick={() => router.push('/mdr')}
            >
              <Shield className="w-5 h-5" />
              <span className="text-[10px]">MDR</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={`flex flex-col items-center space-y-0.5 ${pathname?.startsWith('/vipr') ? getAccentClasses(theme, 'primary') : getTextClasses(theme, 'muted')}`}
              onClick={() => router.push('/vipr')}
            >
              <ShieldAlert className="w-5 h-5" />
              <span className="text-[10px]">VIPR</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={`flex flex-col items-center space-y-0.5 ${pathname?.startsWith('/admin') ? getAccentClasses(theme, 'primary') : getTextClasses(theme, 'muted')}`}
              onClick={() => router.push('/admin')}
            >
              <Lock className="w-5 h-5" />
              <span className="text-[10px]">Admin</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={`flex flex-col items-center space-y-0.5 ${pathname?.startsWith('/settings') ? getAccentClasses(theme, 'primary') : getTextClasses(theme, 'muted')}`}
              onClick={() => router.push('/settings')}
            >
              <Settings className="w-5 h-5" />
              <span className="text-[10px]">Settings</span>
            </Button>
          </div>
        </nav>
      )}

      {/* Footer with Session Info - Fixed at bottom */}
      <div 
        className={`fixed bottom-0 left-0 right-0 w-screen z-50 border-t m-0 p-0 ${getBackgroundClasses(theme, 'default')}`}
        style={{ backgroundColor: 'var(--theme-header-footer)', ...(isMobile ? { height: '60px' } : {}) }}
      >
        <div className={`max-w-7xl mx-auto ${isMobile ? 'px-2 py-1.5' : 'px-4 py-2'}`}>
          {isMobile ? (
            <div className={`flex flex-col space-y-1.5 text-xs ${getTextClasses(theme, 'muted')}`}>
              <div className="flex items-center justify-between">
                <span className="truncate flex-1 min-w-0">Session: {session?.sessionId ? `${session.sessionId.substring(0, 8)}...` : 'Loading...'}</span>
                <div className="ml-2 flex-shrink-0">
                  {connectionStatusBadge}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="truncate">Tenant: {currentTenant?.tenantSlug || 'Loading...'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="truncate text-[10px]">Expires: {formattedExpiryTime}</span>
              </div>
            </div>
          ) : (
            <div className={`flex items-center justify-between text-xs ${getTextClasses(theme, 'muted')}`}>
              <div className="flex-1 flex justify-left space-x-6">
                <span>Session: {session?.sessionId || 'Loading...'}</span>
              </div>
              <div className="flex-1 flex justify-left space-x-6">
               <span>Tenant: {currentTenant?.tenantSlug || 'Loading...'}</span>
              </div>
              <div className="flex-1 flex justify-left">
                <span>Expires: {formattedExpiryTime}</span>
              </div>
              <div className="flex items-center">
                {connectionStatusBadge}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Profile Dialog */}
      <ProfileDialog
        open={profileDialogOpen}
        onOpenChange={setProfileDialogOpen}
        name={displayUserName}
        email={displayUserEmail}
        avatarUrl={displayUserAvatar}
        onAccountSettings={handleSettingsNavigation}
        onLogout={logout}
      />

      {/* Select Tenants Dialog */}
      {selectableTenants.length > 1 && (
        <SelectTenantsDialog
          open={selectTenantsDialogOpen}
          onOpenChange={setSelectTenantsDialogOpen}
          selectableTenants={selectableTenants}
          selectedTenantIds={selectedTenantIds}
          currentTenantId={currentTenant?.tenantId}
          onConfirm={handleSelectTenants}
          isLoading={false}
        />
      )}
    </div>
  );
});

export default AppLayout;